import { useQueries } from '@tanstack/react-query';
import { useContext, useRef } from 'react';

import { AgentChatContext } from '@/components/agent-chat/agent-chat-provider';
import type { ChatArtifactCardModel } from '@/components/chat-artifact-cards';
import { artifactDetailQueryKey } from '@/hooks/queries/artifacts/use-get-artifact';
import { useListArtifacts } from '@/hooks/queries/artifacts/use-list-artifacts';
import type { AgentChatArtifact } from '@/services/agents/chat-stream';
import { getArtifact } from '@/services/artifacts/get-artifact';
import { useOrgStore } from '@/stores/org-store';
import {
  artifactTypeFromFile,
  isArtifactType,
  looksLikeFileName,
} from '@/utils/artifact-type-label';
import type { ArtifactListItem, ArtifactType } from '@/types/artifacts';

const UNAVAILABLE_REASON = 'Download link unavailable (it may have expired)';

function fileNameFromUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  try {
    const path = new URL(url, window.location.origin).pathname;
    return decodeURIComponent(path.split('/').pop() ?? '') || undefined;
  } catch {
    return undefined;
  }
}

/**
 * The file's own name, which drives the card title, its icon and the type
 * label. Frames are inconsistent about where the name lives — some tools only
 * set `file_id` (to the name itself) or nothing but a download URL — so take
 * the first candidate that actually looks like a file name before settling for
 * whatever else is there.
 */
export function chatArtifactFileName(frame: AgentChatArtifact): string {
  const candidates = [
    frame.filename,
    frame.file_name,
    frame.file_id,
    fileNameFromUrl(frame.download_url),
  ];
  return (
    candidates.find(looksLikeFileName) ??
    candidates.find((value): value is string => !!value) ??
    'artifact'
  );
}

export function chatArtifactType(frame: AgentChatArtifact): ArtifactType {
  // The gateway copies `artifact_type` off the Artifact row, so it wins —
  // except for `other`, which is the row's "unclassified" value and loses to
  // anything the file itself can tell us.
  const stored = isArtifactType(frame.artifact_type)
    ? frame.artifact_type
    : undefined;
  if (stored && stored !== 'other') return stored;

  const fromFile = artifactTypeFromFile(
    chatArtifactFileName(frame),
    frame.content_type,
  );
  return fromFile ?? stored ?? 'other';
}

function isExpired(frame: AgentChatArtifact): boolean {
  if (!frame.expires_at) return false;
  const expiresAt = new Date(frame.expires_at).getTime();
  return !Number.isNaN(expiresAt) && expiresAt < Date.now();
}

const THUMBNAIL_STATUSES = new Set<string>([
  'pending',
  'ready',
  'failed',
  'unsupported',
]);

/**
 * Shape a frame like a list row so it renders through the shared card.
 *
 * Promoted frames already carry the Artifact row's `title`, `artifact_type`
 * and `thumbnail_status` (the gateway enriches them), so this covers the card
 * fully until the detail request lands with the thumbnail URL. Stream-only
 * frames have no row at all, so they fall back to the file's own name.
 */
export function frameArtifactItem(
  frame: AgentChatArtifact,
  timestamp: string,
): ArtifactListItem {
  const name = chatArtifactFileName(frame);
  const thumbnailStatus =
    frame.thumbnail_status && THUMBNAIL_STATUSES.has(frame.thumbnail_status)
      ? (frame.thumbnail_status as ArtifactListItem['thumbnail_status'])
      : 'unsupported';
  return {
    id: frame.artifact_id ?? frame.file_id ?? name,
    title: frame.title || name,
    type: chatArtifactType(frame),
    status: 'active',
    thumbnail_status: thumbnailStatus,
    client_data: false,
    owner_id: '',
    created_at: timestamp,
    updated_at: timestamp,
    primary_file_name: name,
    primary_file_mime_type: frame.content_type,
    primary_download_url: frame.download_url,
  };
}

function sourceFileIds(item: ArtifactListItem): string[] {
  return [item.source_chat_file_id, item.source_node_result_file_id].filter(
    (id): id is string => !!id,
  );
}

/**
 * Join a history frame that only stored `file_id` to the Artifact row
 * promotion made for it. Unique filename is a fallback for rows that predate
 * the source columns -- once a row carries one, an unmatched frame must not
 * guess at it by name: `title` is a mutable display label the artifact card
 * can rename, so a legacy-style name match against a row that was already
 * joinable by id would risk landing on an unrelated artifact.
 */
export function resolvePromotedArtifactForFrame(
  frame: AgentChatArtifact,
  artifacts: ArtifactListItem[],
): ArtifactListItem | undefined {
  if (frame.artifact_id) return undefined;
  const fileId = frame.file_id;
  if (fileId) {
    const byFileId = artifacts.find((item) =>
      sourceFileIds(item).includes(fileId),
    );
    if (byFileId) return byFileId;
  }
  const name = chatArtifactFileName(frame);
  const legacyRows = artifacts.filter(
    (item) => sourceFileIds(item).length === 0,
  );
  const byName = legacyRows.filter(
    (item) =>
      item.primary_file_name === name ||
      (!item.primary_file_name && item.title === name),
  );
  return byName.length === 1 ? byName[0] : undefined;
}

/**
 * Resolve chat artifact frames into props for the shared `ArtifactCard`.
 *
 * A promoted frame renders from its gateway enrichment immediately and is
 * upgraded by the artifacts API once that lands, which is what supplies the
 * thumbnail. Frames that only stored a file id open in the artifact pane once
 * the conversation's promoted rows are joined. Stream-only files with no row
 * describe themselves and fall back to the download link.
 */
export function useChatArtifactCards(
  frames: AgentChatArtifact[],
): ChatArtifactCardModel[] {
  const orgId = useOrgStore((state) => state.currentOrg?.orgid);
  const chatContext = useContext(AgentChatContext);
  const conversationId = chatContext?.conversationId ?? null;

  // Stream-only files carry no timestamps, and the card always renders a
  // "last updated" line. Pin one value so it does not drift across renders.
  const seenAt = useRef('');
  if (!seenAt.current) seenAt.current = new Date().toISOString();

  const needsPromotionLookup = frames.some(
    (frame) => !frame.artifact_id && !!frame.file_id,
  );
  // `frames` still renders the previous conversation's transcript for one
  // render after `conversationId` adopts the next one -- the switch clears
  // the id-derived state before the new transcript has loaded, deliberately,
  // so the old question doesn't stay answerable underneath the new chat. A
  // frame that lookup joined by filename in that window would join it
  // against the wrong conversation's rows, so the lookup sits out the switch
  // entirely and lets the frame fall back to its own download link instead.
  const {
    data: conversationArtifacts,
    isLoading: conversationArtifactsLoading,
  } = useListArtifacts(
    { conversation_id: conversationId ?? undefined, limit: 200 },
    {
      enabled:
        !!conversationId &&
        needsPromotionLookup &&
        !chatContext?.isLoadingMessages,
    },
  );

  const promotedFromHistory = new Map<string, ArtifactListItem>();
  for (const frame of frames) {
    if (!frame.file_id || frame.artifact_id) continue;
    const resolved = resolvePromotedArtifactForFrame(
      frame,
      conversationArtifacts?.artifacts ?? [],
    );
    if (resolved) promotedFromHistory.set(frame.file_id, resolved);
  }

  const promotedIds = Array.from(
    new Set(
      [
        ...frames.map((frame) => frame.artifact_id),
        ...[...promotedFromHistory.values()].map((item) => item.id),
      ].filter((id): id is string => !!id),
    ),
  );

  const details = useQueries({
    queries: promotedIds.map((artifactId) => ({
      queryKey: artifactDetailQueryKey(orgId, artifactId),
      queryFn: () => getArtifact(orgId!, artifactId),
      enabled: !!orgId,
      staleTime: 30_000,
      retry: 1,
    })),
  });

  const detailById = new Map(
    promotedIds.map((artifactId, index) => [artifactId, details[index]]),
  );

  return frames.map((frame, index) => {
    const key =
      frame.artifact_id ||
      frame.file_id ||
      frame.download_url ||
      `artifact-${index}`;
    const revision = frame.edited
      ? {
          label: `v${frame.version ?? '?'}`,
          changeSummary: frame.change_summary,
        }
      : undefined;

    const resolved =
      !frame.artifact_id && frame.file_id
        ? promotedFromHistory.get(frame.file_id)
        : undefined;
    const promotedId = frame.artifact_id ?? resolved?.id;
    const detail = promotedId ? detailById.get(promotedId) : undefined;

    if (detail?.data) {
      return {
        key,
        item: detail.data,
        primaryFile: detail.data.files?.find((file) => file.role === 'primary'),
        artifactId: promotedId,
        revision,
      };
    }

    if (promotedId) {
      // The row exists, so the card opens the artifact surface either way.
      // Render from the frame as soon as it says anything about the file, and
      // hold the skeleton only while a silent frame is still resolving.
      const describesFile =
        !!frame.title ||
        !!frame.artifact_type ||
        !!frame.filename ||
        !!frame.file_name ||
        !!resolved;
      return {
        key,
        item:
          detail?.isLoading && !describesFile
            ? null
            : (resolved ?? frameArtifactItem(frame, seenAt.current)),
        artifactId: promotedId,
        revision,
      };
    }

    if (
      needsPromotionLookup &&
      conversationArtifactsLoading &&
      !!frame.file_id
    ) {
      return { key, item: null, revision };
    }

    // Never promoted: no row to preview, so the frame's own link is the only
    // way to reach the file.
    const downloadUrl = isExpired(frame) ? undefined : frame.download_url;
    return {
      key,
      item: frameArtifactItem(frame, seenAt.current),
      downloadUrl,
      unavailableReason: downloadUrl ? undefined : UNAVAILABLE_REASON,
      revision,
    };
  });
}
